import { memo } from 'react'
import { motion } from 'framer-motion'
import { getTicketType } from '../../utils/ticketType'
import { getPriorityMeta } from '../../utils/priorityMatrix'
import { TICKET_ORIGINS } from '../../utils/ticketDraft'
import { employeeName } from '../../utils/employee'
import UserAvatar from '../Shared/UserAvatar'

/** Prioridades a partir de las cuales se destaca la tarjeta (3 Alta, 4 Crítica). */
const HIGHLIGHT_PRIORITY = 3

/**
 * Tarjeta compacta de un ticket en el tablero Kanban: título, una línea de
 * datos (id, tipo, plataforma y prioridad si es alta) y el pie con el autor
 * (foto y nombre) y la fecha límite.
 * El borde izquierdo indica el estado: vencido, finalizado o en curso.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.card - Ticket (id, title, type, systemOrigin, priorityId, dueAt, completed).
 * @param {string} props.columnKey - Columna donde reside la tarjeta.
 * @param {Object} [props.author] - Empleado que creó el ticket.
 * @param {Function} [props.onDragStart] - Recibe (card, columnKey).
 * @param {Function} [props.onSelect] - Recibe (card).
 * @returns {JSX.Element}
 */
const KanbanCard = ({ card, columnKey, author, onDragStart, onSelect }) => {
  const type = getTicketType(card.type)
  const origin = TICKET_ORIGINS.find((o) => o.value === card.systemOrigin)
  const priority = getPriorityMeta(card.priorityId)
  const isLate =
    card.dueAt && new Date(card.dueAt) < new Date() && !card.completed
  const accent = isLate
    ? 'var(--gm-red)'
    : card.completed
      ? 'var(--gm-green)'
      : 'var(--gm-primary)'

  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move'
    onDragStart(card, columnKey)
  }

  return (
    <motion.div
      className="kanban-card"
      draggable={!!onDragStart}
      onDragStart={onDragStart ? handleDragStart : undefined}
      onClick={() => onSelect?.(card)}
      whileTap={{ scale: 0.99 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      layout
      style={{
        cursor: onDragStart ? 'grab' : 'pointer',
        boxShadow: `inset 3px 0 0 ${accent}`,
      }}
      title={card.title}
    >
      <p className="kc-title">{card.title || 'Sin título'}</p>

      <div className="kc-meta">
        <span className="kc-id">#{card.id}</span>
        <span className="kc-item" style={{ color: type.color }}>
          <i className={`fa-solid ${type.icon}`} />
          {type.label}
        </span>
        {card.systemOrigin && (
          <span className="kc-item">
            <i className={`fa-solid ${origin?.icon || 'fa-circle'}`} />
            {card.systemOrigin}
          </span>
        )}
        {card.priorityId >= HIGHLIGHT_PRIORITY && (
          <span className="kc-item" style={{ color: priority.color }}>
            <i className="fa-solid fa-flag" />
            {priority.label}
          </span>
        )}
      </div>

      <div className="kc-foot">
        <span className="kc-author" title="Autor">
          <UserAvatar employee={author} size={20} />
          <span className="kc-author-name">
            {employeeName(author) || 'Autor desconocido'}
          </span>
        </span>
        {card.dueAt && (
          <span className={`kc-item kc-due ${isLate ? 'is-late' : ''}`}>
            <i
              className={`fa-regular ${isLate ? 'fa-clock' : 'fa-calendar'}`}
            />
            {new Date(card.dueAt).toLocaleDateString('es-CR', {
              day: 'numeric',
              month: 'short',
            })}
          </span>
        )}
      </div>

      <style>{`
        .kanban-card {
          background: var(--gm-surface);
          border: 1px solid var(--gm-border);
          border-radius: 8px;
          padding: 9px 11px 9px 13px;
          transition: border-color 0.15s ease;
        }
        .kanban-card:hover { border-color: var(--gm-text-faint); }
        .kc-title {
          margin: 0 0 6px;
          font-size: 0.84rem;
          font-weight: 500;
          line-height: 1.3;
          color: var(--gm-text);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .kc-meta {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 4px 10px;
          font-size: 0.7rem;
          color: var(--gm-text-muted);
        }
        .kc-id { color: var(--gm-text-faint); font-variant-numeric: tabular-nums; }
        .kc-item { display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; }
        .kc-item i { font-size: 0.65rem; }
        .kc-foot {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin-top: 8px;
          font-size: 0.7rem;
          color: var(--gm-text-muted);
        }
        .kc-author { display: inline-flex; align-items: center; gap: 6px; min-width: 0; }
        .kc-author-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--gm-text); }
        .kc-due { flex-shrink: 0; }
        .kc-due.is-late { color: var(--gm-red); font-weight: 600; }
      `}</style>
    </motion.div>
  )
}

export default memo(KanbanCard)
